import React, { useEffect, useRef, useState } from 'react';
import { Sparkles, Upload, Camera, X, Loader2, Download, RefreshCw, AlertCircle, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { api, formatApiError } from '@/lib/api';
import { productImageUrl } from '@/lib/imageUrl';

const STORAGE_KEY_PHOTO = 'tc_vto_photo';     // base64 data URL
const STORAGE_KEY_SESSION = 'tc_vto_session'; // UUID
const STORAGE_KEY_CONSENT = 'tc_vto_consent'; // '1'
const MAX_UPLOAD_MB = 5;

function getOrCreateSession() {
  let s = sessionStorage.getItem(STORAGE_KEY_SESSION);
  if (!s) {
    s = (crypto?.randomUUID?.() || Math.random().toString(36).slice(2) + Date.now());
    sessionStorage.setItem(STORAGE_KEY_SESSION, s);
  }
  return s;
}

async function fileToDataUrl(file) {
  // Downscale to ≤1280px long edge before uploading to keep payload light.
  const img = await new Promise((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = reject;
    i.src = URL.createObjectURL(file);
  });
  const MAX = 1280;
  const ratio = Math.min(1, MAX / Math.max(img.width, img.height));
  const w = Math.round(img.width * ratio);
  const h = Math.round(img.height * ratio);
  const canvas = document.createElement('canvas');
  canvas.width = w; canvas.height = h;
  canvas.getContext('2d').drawImage(img, 0, 0, w, h);
  return canvas.toDataURL('image/jpeg', 0.9);
}

/**
 * Virtual Try-On Button + Modal.
 * - Reuses the same uploaded customer photo across products in the same session
 *   via sessionStorage (auto-cleared on tab close).
 * - Sends photo + product slug to /api/virtual-tryon/generate.
 */
export default function VirtualTryOn({ product, variant = 'card' }) {
  const [open, setOpen] = useState(false);
  const [enabled, setEnabled] = useState(null);
  const [enabledCats, setEnabledCats] = useState([]);
  const [photo, setPhoto] = useState(() => sessionStorage.getItem(STORAGE_KEY_PHOTO) || '');
  const [consent, setConsent] = useState(() => sessionStorage.getItem(STORAGE_KEY_CONSENT) === '1');
  const [result, setResult] = useState('');
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    api.get('/virtual-tryon/settings').then(({ data }) => {
      if (cancelled) return;
      setEnabled(!!data.enabled);
      setEnabledCats(data.enabled_categories || []);
    }).catch(() => setEnabled(false));
    return () => { cancelled = true; };
  }, []);

  const eligible = enabled && product?.category && enabledCats.includes(product.category);
  if (enabled === null || !eligible) return null;

  const onFile = async (file) => {
    if (!file) return;
    if (file.size > MAX_UPLOAD_MB * 1024 * 1024) {
      toast.error(`Photo must be under ${MAX_UPLOAD_MB} MB`);
      return;
    }
    if (!/^image\/(jpeg|png|webp|heic|heif)/i.test(file.type) && !file.name.match(/\.(jpg|jpeg|png|webp|heic|heif)$/i)) {
      toast.error('Please choose a JPG, PNG or WebP image');
      return;
    }
    try {
      const dataUrl = await fileToDataUrl(file);
      setPhoto(dataUrl);
      sessionStorage.setItem(STORAGE_KEY_PHOTO, dataUrl);
      setResult('');
    } catch {
      toast.error('Could not read that image — please try another');
    }
  };

  const clearPhoto = () => {
    setPhoto('');
    setResult('');
    sessionStorage.removeItem(STORAGE_KEY_PHOTO);
  };

  const toggleConsent = () => {
    const next = !consent;
    setConsent(next);
    sessionStorage.setItem(STORAGE_KEY_CONSENT, next ? '1' : '0');
  };

  const generate = async () => {
    if (!photo) { toast.error('Please upload a full-body photo first'); return; }
    if (!consent) { toast.error('Please accept the privacy notice first'); return; }
    setLoading(true);
    setResult('');
    try {
      const { data } = await api.post('/virtual-tryon/generate', {
        product_slug: product.slug,
        session_id: getOrCreateSession(),
        customer_photo_b64: photo,
        consent: true,
      });
      setResult(data.result_data_url);
      toast.success('Your try-on is ready');
    } catch (e) {
      toast.error(formatApiError(e, 'Try-on failed. Please try a clearer full-body photo.'));
    } finally {
      setLoading(false);
    }
  };

  const download = () => {
    if (!result) return;
    const a = document.createElement('a');
    a.href = result;
    a.download = `trendy-closet-tryon-${product.slug}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  // ------- Button -------
  const btnClass = variant === 'card'
    ? 'inline-flex items-center gap-1.5 px-3 py-2 bg-maroon/90 text-ivory text-[10px] uppercase tracking-[0.18em] hover:bg-maroon transition-colors'
    : 'inline-flex items-center justify-center gap-2 w-full px-6 py-3 border border-maroon text-maroon hover:bg-maroon hover:text-ivory text-xs uppercase tracking-[0.22em] transition-colors';

  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
        className={btnClass}
        data-testid={`vto-open-${product.slug}`}
      >
        <Sparkles className="w-3.5 h-3.5" />
        Try It On
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => !loading && setOpen(false)}
            data-testid="vto-modal"
          >
            <motion.div
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}
              className="w-full max-w-5xl max-h-[92vh] overflow-y-auto bg-ivory border border-brandborder shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-5 lg:px-7 py-4 border-b border-brandborder">
                <div className="flex items-center gap-3">
                  <Sparkles className="w-4 h-4 text-maroon" />
                  <div>
                    <div className="overline text-charcoal/60">Virtual Try-On</div>
                    <div className="font-display text-lg text-maroon leading-tight">{product.label}</div>
                  </div>
                </div>
                <button onClick={() => !loading && setOpen(false)} aria-label="Close" className="text-charcoal/60 hover:text-charcoal">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-5 lg:p-7">
                {/* ---- LEFT: Photo + controls ---- */}
                <div className="space-y-5">
                  <div>
                    <div className="overline text-charcoal/60 mb-2">Your Photo</div>
                    {!photo ? (
                      <div className="border-2 border-dashed border-brandborder/70 p-6 text-center bg-white" data-testid="vto-dropzone">
                        <ImageIcon className="w-8 h-8 text-charcoal/30 mx-auto mb-3" />
                        <div className="text-sm text-charcoal/70 mb-4">Full-body, front-facing. Good lighting, plain background works best.</div>
                        <div className="flex flex-col sm:flex-row gap-2 justify-center">
                          <button
                            onClick={() => fileInputRef.current?.click()}
                            data-testid="vto-upload-btn"
                            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-maroon text-ivory hover:bg-maroon/90 text-xs uppercase tracking-[0.18em] transition-colors"
                          >
                            <Upload className="w-4 h-4" /> Upload Photo
                          </button>
                          <button
                            onClick={() => cameraInputRef.current?.click()}
                            data-testid="vto-camera-btn"
                            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 border border-maroon text-maroon hover:bg-maroon hover:text-ivory text-xs uppercase tracking-[0.18em] transition-colors"
                          >
                            <Camera className="w-4 h-4" /> Take Photo
                          </button>
                        </div>
                        <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} />
                        <input ref={cameraInputRef} type="file" accept="image/*" capture="user" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} />
                      </div>
                    ) : (
                      <div className="relative border border-brandborder bg-white" data-testid="vto-photo-preview">
                        <img src={photo} alt="Your photo" className="w-full h-auto max-h-[420px] object-contain" />
                        <button
                          onClick={clearPhoto}
                          data-testid="vto-replace-btn"
                          className="absolute top-2 right-2 inline-flex items-center gap-1.5 px-3 py-1.5 bg-ivory/95 border border-brandborder hover:bg-ivory text-[10px] uppercase tracking-[0.18em] text-charcoal transition-colors"
                        >
                          <RefreshCw className="w-3 h-3" /> Replace
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Consent */}
                  <label className="flex items-start gap-2 text-xs text-charcoal/80 cursor-pointer" data-testid="vto-consent">
                    <input type="checkbox" checked={consent} onChange={toggleConsent} className="mt-0.5 accent-maroon" />
                    <span>
                      I consent to my photo being sent to our AI partner for try-on generation. Photos are stored for up to 24 hours in my private session and then auto-deleted. See the disclaimer below.
                    </span>
                  </label>

                  <button
                    onClick={generate}
                    disabled={!photo || !consent || loading}
                    data-testid="vto-generate-btn"
                    className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 bg-maroon text-ivory hover:bg-maroon/90 text-xs uppercase tracking-[0.2em] transition-colors disabled:bg-maroon/40 disabled:cursor-not-allowed"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                    {loading ? 'Generating… this takes 10–30 s' : 'Generate My Look'}
                  </button>

                  <div className="flex items-start gap-2 text-[11px] text-charcoal/55 leading-relaxed">
                    <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                    <span>Virtual Try-On is an AI-generated visual preview. Actual fit, fabric drape, colours and appearance may vary.</span>
                  </div>
                </div>

                {/* ---- RIGHT: Result + outfit ref ---- */}
                <div className="space-y-5">
                  <div>
                    <div className="overline text-charcoal/60 mb-2">Try-On Result</div>
                    <div className="relative border border-brandborder bg-white aspect-[4/5] flex items-center justify-center overflow-hidden" data-testid="vto-result">
                      {loading && (
                        <div className="flex flex-col items-center gap-3 text-charcoal/60">
                          <Loader2 className="w-6 h-6 animate-spin text-maroon" />
                          <div className="text-xs">Rendering your look…</div>
                        </div>
                      )}
                      {!loading && result && (
                        <img src={result} alt="Your virtual try-on" className="w-full h-full object-contain" />
                      )}
                      {!loading && !result && (
                        <div className="text-xs text-charcoal/40 px-6 text-center">Your generated look will appear here.</div>
                      )}
                    </div>
                    {result && (
                      <div className="mt-3 grid grid-cols-2 gap-2">
                        <button onClick={download} data-testid="vto-download-btn" className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-charcoal text-ivory hover:bg-charcoal/90 text-[11px] uppercase tracking-[0.18em] transition-colors">
                          <Download className="w-3.5 h-3.5" /> Download
                        </button>
                        <button onClick={generate} data-testid="vto-regenerate-btn" className="inline-flex items-center justify-center gap-2 px-4 py-2 border border-brandborder hover:border-maroon hover:text-maroon text-[11px] uppercase tracking-[0.18em] transition-colors">
                          <RefreshCw className="w-3.5 h-3.5" /> Regenerate
                        </button>
                      </div>
                    )}
                  </div>

                  <div>
                    <div className="overline text-charcoal/60 mb-2">Outfit</div>
                    <div className="border border-brandborder bg-white">
                      {productImageUrl(product) ? (
                        <img src={productImageUrl(product)} alt={product.label} className="w-full h-auto max-h-56 object-contain bg-ivory/40" />
                      ) : (
                        <div className="h-32 flex items-center justify-center text-xs text-charcoal/40">No image</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
